import { useWorkspaceText } from './interface-copy';
import { isApplePlatform } from '@/desktop-downloads/platform';
import { Fragment, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { Dialog } from 'radix-ui';
import { CircleAlertIcon } from 'lucide-react';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import searchCopy, { type SearchCopyKey } from '@/i18n/ns/search';
import { useNarrowViewport } from '@/lib/mobile';
import { useWorkspace } from './context';
import { workspaceResultCanvasTarget } from './artifact-panels/open-target';
import { findWorkspaceActions, type FinderAction, type FinderActionId } from './finder-actions';
import {
  groupSearchHits,
  searchSnippet,
  type SearchGroupId,
  type SearchSnippet,
} from './finder-results';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { useIntlLocale } from './intl-locale';
import type { FinderItem, WorkspaceFindHit } from './types';
import { writeWorkspaceHash } from '@/lib/chat-links';
import { useFinderSearch } from './finder-search';
import { WorkspaceUploadInput } from './upload-input';
import { libraryUploads } from './library-feed/upload-queue';

type SearchCopy = Readonly<Record<SearchCopyKey, string>>;
// `metadata` keeps a Result hit's routing hint: canonical saved work (an App, a
// saved Document or Report) opens at its named version, never as a legacy Result.
type WorkRow = Pick<FinderItem, 'id' | 'kind' | 'title'> &
  Pick<WorkspaceFindHit, 'metadata'> & { snippet: SearchSnippet };

const icons: Record<FinderItem['kind'], WorkspaceIconName> = {
  chat: 'chat',
  file: 'file',
  skill: 'skills',
  skill_draft: 'skills',
  result: 'results',
  knowledge_page: 'knowledge',
  meeting: 'meetings',
};
const kindLabels: Record<FinderItem['kind'], SearchCopyKey> = {
  chat: 'kindChat',
  file: 'kindFile',
  skill: 'kindSkill',
  skill_draft: 'kindSkillDraft',
  result: 'kindResult',
  knowledge_page: 'kindKnowledgePage',
  meeting: 'kindMeeting',
};
/** A hit of a kind the Finder shows. The server may still return retired kinds
 * (company facts, sources) while they are being removed; those are skipped. */
const isFinderKind = (kind: string): kind is FinderItem['kind'] => Object.hasOwn(icons, kind);
const groupLabels: Record<SearchGroupId, SearchCopyKey> = {
  chats: 'groupChats',
  results: 'groupResults',
  files: 'groupFiles',
  skills: 'groupSkills',
  knowledge: 'groupKnowledge',
  meetings: 'groupMeetings',
};
const SEARCH_KINDS: FinderItem['kind'][] = [
  'knowledge_page',
  'skill',
  'skill_draft',
  'result',
  'file',
  'chat',
  'meeting',
];

type Entry =
  | { type: 'action'; key: string; value: FinderAction }
  | { type: 'work'; key: string; value: WorkRow }
  | { type: 'ask'; key: string; query: string };
interface Section {
  id: string;
  label: string;
  entries: Entry[];
  /** The last answer's rows, kept in place while the next one loads: shown, never chosen. */
  held?: boolean;
}
interface FinderView {
  actions: FinderAction[];
  work: WorkRow[];
  loading: boolean;
  error: string;
  hasMore: boolean;
  loadMore: () => void;
}
/** Radix keeps the dialog mounted for its exit animation, but closing already
 * emptied the conversation search. Hold what the open dialog showed until it is
 * gone, so nothing reorders, disappears or flashes behind the close, and never
 * into the next open.
 *
 * A view is never held across an owner change (user or company). The render
 * that first sees a new owner is still an open one carrying the previous
 * owner's workspace rows, because the provider empties them and closes the
 * Finder from an effect; so the freeze is dropped there and that close renders
 * live. The ref is written and read during render: sound only because the write
 * is idempotent and no open render is discarded here (no StrictMode,
 * transition or Suspense boundary around the Finder). */
function useClosingView(open: boolean, live: FinderView, owner: string) {
  const frozen = useRef<{ owner: string; view: FinderView | null }>({ owner, view: null });
  if (frozen.current.owner !== owner) frozen.current = { owner, view: null };
  else if (open) frozen.current.view = live;
  return open ? live : (frozen.current.view ?? live);
}

/** The work rows: the recent list (empty query) or the results grouped by kind. */
function workSections(view: FinderView, query: string, copy: SearchCopy): Section[] {
  const work = (value: WorkRow): Entry => ({
    type: 'work',
    key: `${value.kind}:${value.id}`,
    value,
  });
  if (!query.trim())
    return view.work.length
      ? [{ id: 'recent', label: copy.groupRecent, entries: view.work.map(work) }]
      : [];
  return groupSearchHits(view.work).map((group) => ({
    id: group.id,
    label: copy[groupLabels[group.id]],
    entries: group.items.map(work),
  }));
}

/** Actions first, then the work rows; a search that finds nothing offers to ask
 * Poneva instead. */
function searchSections(
  view: FinderView,
  work: Section[],
  query: string,
  copy: SearchCopy,
): Section[] {
  const sections: Section[] = [];
  if (view.actions.length)
    sections.push({
      id: 'actions',
      label: copy.groupActions,
      entries: view.actions.map((value) => ({ type: 'action', key: `action:${value.id}`, value })),
    });
  sections.push(...work);
  const trimmed = query.trim();
  if (trimmed && !sections.length && !view.loading && !view.error)
    sections.push({
      id: 'ask',
      label: formatCopy(copy.noResults, { query: trimmed }),
      entries: [{ type: 'ask', key: `ask:${trimmed}`, query: trimmed }],
    });
  return sections;
}

/** While the next answer loads, the last answer's rows stay where they were,
 * dimmed and inert, so the palette keeps its height and nothing reorders; the
 * new answer replaces them in one step. Every open, and every owner change,
 * starts empty, so no row carries into the next open or across owners. The
 * ref is written during render on the terms useClosingView states: each write
 * depends only on that render's inputs. */
function useHeldSections(open: boolean, owner: string, live: Section[], pending: boolean) {
  const held = useRef({ open, owner, sections: [] as Section[] });
  if (held.current.owner !== owner || (open && !held.current.open))
    held.current = { open, owner, sections: [] };
  held.current.open = open;
  if (!pending) held.current.sections = live;
  return pending ? held.current.sections.map((section) => ({ ...section, held: true })) : live;
}

/** Rows in the shape of results while the first answer loads (DESIGN-SPEC §6.15). */
function SearchSkeleton() {
  return (
    <div className="ws-finder-skeleton" aria-hidden="true">
      <span className="ws-finder-skeleton-label" />
      {[0, 1, 2].map((row) => (
        <span key={row} className="ws-finder-skeleton-row">
          <i />
          <i />
        </span>
      ))}
    </div>
  );
}

/** A failed search in words people use; the cause stays one click away. */
function SearchError({
  cause,
  copy,
  onRetry,
}: {
  cause: string;
  copy: SearchCopy;
  onRetry: () => void;
}) {
  return (
    <div className="ws-finder-error" role="alert">
      <CircleAlertIcon aria-hidden="true" />
      <p>{copy.failed}</p>
      <button type="button" onClick={onRetry}>
        {copy.retry}
      </button>
      <details>
        <summary>{copy.details}</summary>
        <p>{cause}</p>
      </details>
    </div>
  );
}

/** Ask leaves its question in the new chat's composer: the caret goes to its end. */
function focusComposer() {
  const composer = document.querySelector<HTMLTextAreaElement>('.aui-composer-input');
  if (!composer) return;
  composer.focus();
  composer.setSelectionRange(composer.value.length, composer.value.length);
}

/** "26 Sept", with the year only when it is not this year, in the app language. */
function shortDate(value: string, locale: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

export function Snippet({
  snippet,
  copy,
  locale,
  className = 'ws-finder-excerpt',
}: {
  snippet: SearchSnippet;
  copy: SearchCopy;
  locale: string;
  className?: string;
}) {
  if (!snippet) return null;
  if (snippet.type === 'updated') {
    const date = shortDate(snippet.at, locale);
    return date ? <small className={className}>{formatCopy(copy.updated, { date })}</small> : null;
  }
  return (
    <small className={className}>
      {snippet.parts.map((part, index) => (
        <Fragment key={index}>{part.match ? <mark>{part.text}</mark> : part.text}</Fragment>
      ))}
    </small>
  );
}

function entryView(entry: Entry, copy: SearchCopy) {
  if (entry.type === 'action')
    return { icon: entry.value.icon, title: entry.value.title, type: 'action', kind: null };
  if (entry.type === 'ask')
    return {
      icon: 'chat' as const,
      title: formatCopy(copy.ask, { query: entry.query }),
      type: 'ask',
      kind: null,
    };
  return {
    icon: icons[entry.value.kind],
    title: entry.value.title,
    type: entry.value.kind,
    kind: copy[kindLabels[entry.value.kind]],
  };
}

/** One row. A held row (`index` null) is the previous answer's, drawn in place
 * while the next loads: it is not an option and cannot be chosen. */
function SearchOption({
  entry,
  index,
  selected,
  copy,
  locale,
  onChoose,
  onPoint,
}: {
  entry: Entry;
  index: number | null;
  selected: boolean;
  copy: SearchCopy;
  locale: string;
  onChoose: (entry: Entry) => void;
  onPoint: (index: number) => void;
}) {
  const view = entryView(entry, copy);
  const live = index !== null;
  return (
    <button
      className="ws-finder-row"
      role={live ? 'option' : undefined}
      aria-selected={live ? selected : undefined}
      id={live ? `ws-found-${index}` : undefined}
      data-index={live ? index : undefined}
      data-result-type={view.type}
      title={view.title}
      onClick={() => {
        if (live) onChoose(entry);
      }}
      // Only a moving mouse moves the selection; a finger scrolling the list does not.
      onPointerMove={(event: PointerEvent) => {
        if (live && event.pointerType === 'mouse' && !selected) onPoint(index);
      }}
    >
      <WorkspaceIcon name={view.icon} size={16} />
      <span className="ws-finder-match-copy">
        <strong>{view.title}</strong>
        {entry.type === 'action' && <small>{entry.value.description}</small>}
        {entry.type === 'work' && (
          <Snippet snippet={entry.value.snippet} copy={copy} locale={locale} />
        )}
      </span>
      {view.kind && <span className="ws-finder-kind ws-sr-only">{view.kind}</span>}
    </button>
  );
}

function SearchFooter({ copy }: { copy: SearchCopy }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <footer className="ws-finder-footer" aria-hidden="true">
      <span>
        <kbd>↑</kbd>
        <kbd>↓</kbd>
        {copy.navigate}
      </span>
      <span>
        <kbd>↵</kbd>
        {copy.open}
      </span>
      <span>
        <kbd>{uiText('Esc')}</kbd>
        {copy.dismiss}
      </span>
    </footer>
  );
}

/** The sidebar's Search entry; ⌘K / Ctrl+K opens the same dialog. On a phone the
 * drawer closes first, so Search is the only thing over the page. */
export function SearchNavItem() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const apple = isApplePlatform(navigator);
  const copy = useNamespaceCopy(searchCopy);
  return (
    <button
      className="ws-nav-item ws-find-button"
      onClick={() => {
        ws.setMobileOpen(false);
        ws.setFinderOpen(true);
      }}
      title={copy.name}
      aria-label={copy.name}
      aria-keyshortcuts="Meta+K Control+K"
    >
      <WorkspaceIcon name="search" size={18} />
      <span>{copy.name}</span>
      {/* The platform's own modifier; touch devices have no shortcut to show (CSS hides it). */}
      <kbd aria-hidden="true">{apple ? <WorkspaceIcon name="command" size={11} /> : 'Ctrl '}K</kbd>
    </button>
  );
}

/** One piece of state per list: the selection starts on the first row whenever
 * the query or the first row changes, and survives rows appended below it. */
function useSelection(entries: Entry[], query: string) {
  const listKey = `${query}\0${entries[0]?.key ?? ''}`;
  const last = entries.length - 1;
  const [selection, setSelection] = useState({ key: '', index: 0 });
  const current = (state: typeof selection) =>
    state.key === listKey ? Math.min(state.index, last) : 0;
  const active = entries.length ? current(selection) : -1;
  const select = (index: number) => setSelection({ key: listKey, index });
  // Functional, so a key held down moves one row per event even between renders.
  const move = (step: number) =>
    setSelection((state) => ({
      key: listKey,
      index: Math.max(0, Math.min(last, current(state) + step)),
    }));
  return { active, select, move, listKey };
}

export function WorkspaceFinder() {
  const ws = useWorkspace();
  const company = useCompany();
  const copy = useNamespaceCopy(searchCopy);
  const locale = useIntlLocale();
  const narrow = useNarrowViewport();
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const owner = `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`;
  const retrieval = useFinderSearch(
    Boolean(ws.finderOpen && company.data?.company?.id),
    query,
    SEARCH_KINDS,
    owner,
  );
  const found = useMemo(
    () =>
      retrieval.hits.flatMap((hit): WorkRow[] =>
        isFinderKind(hit.kind)
          ? [
              {
                id: hit.id,
                kind: hit.kind,
                title: hit.title,
                metadata: hit.metadata,
                snippet: query.trim() ? searchSnippet(hit, query, locale, { narrow }) : null,
              },
            ]
          : [],
      ),
    [retrieval.hits, query, locale, narrow],
  );
  const foundActions = findWorkspaceActions(query, copy, locale).filter(
    (action) => action.id !== 'upload-files' || !ws.busy,
  );
  const view = useClosingView(
    ws.finderOpen,
    {
      actions: foundActions,
      work: found,
      loading: retrieval.loading,
      error: retrieval.error,
      hasMore: retrieval.hasMore,
      loadMore: retrieval.loadMore,
    },
    // The provider's own scope: what it reloads the workspace for.
    owner,
  );
  // A new query is loading and nothing of its answer has arrived yet.
  const pending = view.loading && !view.work.length;
  const work = useHeldSections(
    ws.finderOpen,
    owner,
    pending ? [] : workSections(view, query, copy),
    pending,
  );
  const sections = searchSections(view, work, query, copy);
  const entries = sections.filter((section) => !section.held).flatMap((section) => section.entries);
  const { active, select, move, listKey } = useSelection(entries, query);
  // Enter pressed before the answer arrived opens that answer's first row.
  const waiting = useRef<string | null>(null);
  // Ask closes Search into the composer holding its question, not back to the trigger.
  const toComposer = useRef(false);
  useEffect(() => {
    waiting.current = null;
    if (ws.finderOpen) setQuery('');
  }, [ws.finderOpen]);
  // A new list starts at its top; only the keyboard scrolls the selection into view,
  // so a row under the mouse never moves the list.
  const keyboard = useRef(false);
  useEffect(() => {
    scroll.current?.scrollTo?.({ top: 0 });
  }, [listKey]);
  useEffect(() => {
    if (!keyboard.current) return;
    keyboard.current = false;
    if (active <= 0) scroll.current?.scrollTo?.({ top: 0 });
    else
      scroll.current
        ?.querySelector(`[data-index="${active}"]`)
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);
  const open = (item: WorkRow) => {
    ws.setFinderOpen(false);
    if (item.kind === 'chat') ws.openThread(item.id);
    else if (item.kind === 'knowledge_page') ws.openCanvas({ kind: 'page', id: item.id });
    // A Skill draft opens in its Skill's canvas, which shows the draft.
    else if (item.kind === 'skill_draft') ws.openCanvas({ kind: 'skill', id: item.id });
    else if (item.kind === 'result') void openResult(item);
    else ws.openCanvas({ kind: item.kind, id: item.id });
  };
  // Search opens Result rows through the Library's seam: saved work resolves its current version first.
  const openResult = async (item: WorkRow) => {
    const ownsNavigation = ws.beginNavigation();
    const target = await workspaceResultCanvasTarget(item).catch(() => null);
    if (!ownsNavigation()) return;
    if (target) ws.openCanvas(target);
    else ws.notify(copy.openFailed, null, 'error');
  };
  const runAction = (action: FinderActionId) => {
    if (action === 'new-chat') ws.newChat();
    else if (action === 'create-skill') ws.authorSkill();
    else if (!ws.busy) uploadInput.current?.click();
  };
  const choose = (entry: Entry) => {
    if (entry.type === 'action') runAction(entry.value.id);
    // Nothing matched: the question waits in a new chat's composer, and the
    // person sends it. Search text (a typo, a keyword) never spends a turn alone.
    else if (entry.type === 'ask') {
      toComposer.current = true;
      ws.setFinderOpen(false);
      ws.send(entry.query, undefined, false, null, { newChat: true });
    } else open(entry.value);
  };
  useEffect(() => {
    const wanted = waiting.current;
    if (wanted === null || pending) return;
    waiting.current = null;
    if (ws.finderOpen && wanted === query.trim() && entries[0]) choose(entries[0]);
  }, [pending]);
  let index = 0;
  return (
    <Dialog.Root open={ws.finderOpen} onOpenChange={ws.setFinderOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="ws-finder-scrim pc-portal" />
        <Dialog.Content
          className="ws-finder pc-portal"
          aria-describedby="ws-finder-description"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            input.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            if (!toComposer.current) return;
            toComposer.current = false;
            event.preventDefault();
            focusComposer();
          }}
        >
          <Dialog.Title className="ws-sr-only">{copy.name}</Dialog.Title>
          <Dialog.Description id="ws-finder-description" className="ws-sr-only">
            {copy.description}
          </Dialog.Description>
          <div className="ws-finder-search">
            <WorkspaceIcon name="search" size={16} />
            <input
              ref={input}
              type="text"
              aria-label={copy.placeholder}
              role="combobox"
              aria-controls="ws-finder-results"
              aria-expanded="true"
              aria-activedescendant={entries[active] ? `ws-found-${active}` : undefined}
              placeholder={copy.placeholder}
              maxLength={300}
              enterKeyHint="go"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  keyboard.current = true;
                  move(e.key === 'ArrowDown' ? 1 : -1);
                }
                if (e.key !== 'Enter') return;
                if (entries[active]) {
                  e.preventDefault();
                  choose(entries[active]);
                } else if (pending) {
                  e.preventDefault();
                  waiting.current = query.trim();
                }
              }}
            />
            <Dialog.Close className="ws-finder-close" aria-label={copy.close}>
              <WorkspaceIcon name="close" size={16} />
            </Dialog.Close>
            <WorkspaceUploadInput
              ref={uploadInput}
              label={copy.uploadLabel}
              disabled={ws.busy}
              testId="finder-file-input"
              onFiles={(files) => {
                if (ws.busy) return;
                ws.setFinderOpen(false);
                // Uploads are rows in Library → Files: the files go there, each showing its own progress.
                libraryUploads.add(Array.from(files));
                ws.navigate('files');
              }}
            />
          </div>
          <div className="ws-finder-scroll" ref={scroll}>
            {retrieval.degraded && (
              <p className="ws-finder-notice" role="status">
                {copy.limited}
              </p>
            )}
            {view.loading && (
              <p className="ws-finder-status ws-sr-only" role="status">
                {query.trim() ? copy.searching : copy.loading}
              </p>
            )}
            {view.error && (
              <SearchError cause={view.error} copy={copy} onRetry={() => retrieval.retry()} />
            )}
            <div
              className="ws-finder-results"
              id="ws-finder-results"
              role="listbox"
              aria-busy={view.loading}
              aria-label={copy.results}
            >
              {sections.map((section) => (
                <div
                  key={section.id}
                  className="ws-finder-group"
                  role={section.held ? undefined : 'group'}
                  aria-labelledby={section.held ? undefined : `ws-finder-group-${section.id}`}
                  aria-hidden={section.held || undefined}
                  inert={section.held}
                  data-held={section.held || undefined}
                >
                  <p
                    className="ws-finder-group-label"
                    id={section.held ? undefined : `ws-finder-group-${section.id}`}
                  >
                    {section.label}
                  </p>
                  {section.entries.map((entry) => {
                    const position = section.held ? null : index++;
                    return (
                      <SearchOption
                        key={entry.key}
                        entry={entry}
                        index={position}
                        selected={position !== null && position === active}
                        copy={copy}
                        locale={locale}
                        onChoose={choose}
                        onPoint={(position) => {
                          keyboard.current = false;
                          select(position);
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            {pending && !work.length && <SearchSkeleton />}
            {!view.loading && !view.error && view.hasMore && (
              <div className="ws-finder-more">
                <button onClick={view.loadMore}>{copy.loadMore}</button>
              </div>
            )}
          </div>
          <SearchFooter copy={copy} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
